import { chain, isDate, isNil, last, orderBy } from 'lodash'
import { curry } from 'lodash/fp'
import { cond, either, evolve, identity, isEmpty, T } from 'ramda'
import { TemporalEntityId } from 'utils/api'
import { getToday, transformApiDate } from 'utils/date'

import { DateTrustCode, Entity } from '../../common/types'
import { FundingRound } from '../../fundingRounds/types'
import { PerformanceIndicator, PerformanceIndicatorType } from '../../performanceIndicators/types'
import { Industry, IndustryType, LocationType, Organization, OrganizationData } from '../types'

export const CATEGORIES_SEPARATOR = '  │  '
export const NA = 'Not available'

export const commarizeCurrency = (num: number): string => {
  const n = Math.abs(num)
  return n >= 1.0e9
    ? (n / 1.0e9).toFixed(2) + 'B'
    : n >= 1.0e6
    ? (n / 1.0e6).toFixed(2) + 'M'
    : n >= 1.0e3
    ? (n / 1.0e3).toFixed(2) + 'K'
    : n.toString()
}

export const isEmptyOrUndefined: (value: any) => boolean = either(isEmpty, isNil)

export const toIntlDate = (value: Date, options: Intl.DateTimeFormatOptions) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    localeMatcher: options.localeMatcher,
    weekday: options.weekday,
    era: options.era,
    year: options.year,
    month: options.month,
    day: options.day,
    hour: options.hour,
    minute: options.minute,
    second: options.second,
    timeZoneName: options.timeZoneName,
    formatMatcher: options.formatMatcher,
    hour12: options.hour12,
    timeZone: options.timeZone ?? 'GMT',
  })
  return formatter.format(value)
}

export const toCurrency = (value: number | string) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    maximumSignificantDigits: 1,
  })
  if (typeof value === 'number') {
    return formatter.format(value)
  }
  return formatter.format(parseInt(value))
}

export const trxMoneyAndDate = evolve({
  moneyRaised: cond([
    [(mr) => mr > 0, commarizeCurrency],
    [T, identity as () => string],
  ]),
  date: (d) => toIntlDate(new Date(d), { month: 'long', year: 'numeric' }),
})

export const organizationToString = ({ name, domain }: Organization) => name + (domain ? ` (${domain})` : '')

export const displayIndustry = ({ name }: Industry) => name

const displayOrganizationIndustriesOfType = curry(
  (type: IndustryType | undefined, { industriesRelations }: Partial<Organization>) =>
    !!industriesRelations
      ? industriesRelations
          .filter(({ industry }) => (type ? industry.type === type : true))
          .map(({ industry }) => industry.name)
          .join(CATEGORIES_SEPARATOR)
      : '–'
)

export const displayOrganizationIndustries = displayOrganizationIndustriesOfType(undefined)
export const displayOrganizationSectors = displayOrganizationIndustriesOfType(IndustryType.sector)
export const displayOrganizationEnablers = displayOrganizationIndustriesOfType(IndustryType.enabler)
export const displayOrganizationBusinessModels = displayOrganizationIndustriesOfType(IndustryType.businessModel)

export const getOrganizationStage = (organization: Partial<Organization>) => {
  return organization.currentStageData?.currentStage
}

export function getOrganizationLastFundingRound(organization: Partial<Organization>): FundingRound | undefined {
  const announcedFundingRounds = (organization.fundingRounds ?? []).filter(
    (fundingRounds) => !!fundingRounds.announcedOn
  )

  const orderedFundingRounds = orderBy(
    announcedFundingRounds,
    (fundingRound) => fundingRound.announcedOn && new Date(fundingRound.announcedOn)
  )

  return last(orderedFundingRounds)
}

export const displayOrganizationTypes = ({ types }: Partial<Organization>) =>
  !!types ? types.map((type) => type).join(CATEGORIES_SEPARATOR) : '–'

export const getOrganizationCurrentRoundType = ({ currentRoundType }: Partial<Organization>) => currentRoundType

export const getOrganizationBlurb = ({ blurb }: Organization) => blurb

export const getOrganizationSignalAssessments = ({ signalAssessments }: Organization) => signalAssessments || []

export const getOrganizationRejectedInvestors = ({ rejectedInvestors }: Organization) =>
  rejectedInvestors ? [rejectedInvestors] : []

export const getOrganizationInvestorsInCapTable = ({ investorsInCapTable }: Organization) =>
  investorsInCapTable ? [investorsInCapTable] : []

const handleTime = (date?: Date) => (date != null ? new Date(date).getTime() : 0)
export const sortFundingRoundsByDate = (fundingRoundA: FundingRound, fundingRoundB: FundingRound) => {
  const fundingRoundADate = fundingRoundA.announcedOn ? new Date(fundingRoundA.announcedOn) : undefined
  const fundingRoundBDate = fundingRoundB.announcedOn ? new Date(fundingRoundB.announcedOn) : undefined

  return handleTime(fundingRoundADate) - handleTime(fundingRoundBDate)
}
export const isFundingRoundClosed = ({ announcedOn }: FundingRound) => !!announcedOn
export const getFundingRoundSize = ({ roundSize }: FundingRound) => Number(roundSize) || undefined
export const getTotalFundingRaised = (fundingRounds: FundingRound[] = []) =>
  chain(fundingRounds).filter(isFundingRoundClosed).map(getFundingRoundSize).compact().sum().value()
export const getFormattedTotalFundingRaised = (fundingRounds: FundingRound[] = []) =>
  commarizeCurrency(getTotalFundingRaised(fundingRounds))
export const getLastClosedFundingRound = (fundingRounds: FundingRound[] = []) =>
  chain(fundingRounds).filter(isFundingRoundClosed).sort(sortFundingRoundsByDate).last().value()

export const getCurrentFundingRound = ({ fundingRounds = [] }: Organization): FundingRound | undefined =>
  fundingRounds.find(({ announcedOn }) => isNil(announcedOn))

export const getPastFundingRounds = ({ fundingRounds = [] }: Organization): FundingRound[] =>
  fundingRounds.filter(({ announcedOn }) => !isNil(announcedOn))

export const getFundingRoundMatchingStage = (
  organization: Organization,
  stage: string | undefined = getOrganizationStage(organization)
) => {
  if (!stage) {
    return undefined
  }
  const stageRegExp = new RegExp(stage, 'i')
  const stageFundingRound = chain(organization.fundingRounds)
    .filter(({ announcedOn }) => isDate(announcedOn))
    .filter(({ roundType }) => (roundType ? stageRegExp.test(roundType) : false))
    .sortBy(({ announcedOn }) => announcedOn)
    .reverse()
    .first()
    .value()
  return stageFundingRound
}

export const getFundingRoundCommittedInvestors = ({ committedInvestors }: FundingRound) => committedInvestors || ''

export function getFundingRoundCommittedInvestorsAsPerformanceIndicator({
  committedInvestors,
  createdAt,
}: Pick<FundingRound, 'committedInvestors'> & Entity): PerformanceIndicator | undefined {
  // TODO: We transform it to performance indicator for simplicity, but measuredOn is inconsistent
  // A safe heuristic would be the createdAt date of the funding round
  if (!committedInvestors) {
    return undefined
  }
  return {
    id: TemporalEntityId.unset,
    value: committedInvestors,
    type: PerformanceIndicatorType.committedInvestors,
    measuredOn: transformApiDate(createdAt) || getToday(),
    measuredOnTrustCode: DateTrustCode.yearDayMonth,
  }
}

export function getOrganizationIndustries({ industriesRelations }: Organization | OrganizationData) {
  return industriesRelations || []
}

export function getOrganizationHeadquarters({ locationsRelations = [] }: Organization | OrganizationData) {
  return locationsRelations.filter(
    (locationRelation) => locationRelation.type === LocationType.headquarters && !isNil(locationRelation.location)
  )
}

export function getOrganizationOperatingLocations({ locationsRelations = [] }: Organization | OrganizationData) {
  return locationsRelations.filter(
    (locationRelation) => locationRelation.type === LocationType.operating && !isNil(locationRelation.location)
  )
}
